import React, { useMemo, useState } from "react";
import {
  View,
  Text,
  Image,
  TouchableOpacity,
  StyleSheet,
  Dimensions,
  Platform,
} from "react-native";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store";
import { useTheme } from "../../theme";
import { ThemeColors } from "../../theme/types";
import LHConfirmationDialog from "../../components/LHConfirmationDialog";
import { resetPulseCheck } from "../../store/slices/pulseCheckSlice";
import { resetObservation } from "../../store/slices/observationSlice";
import DLOBreadcrumb, {
  DLOBreadcrumbItem,
} from "../../modules/dlo/components/DLOBreadcrumb";
import FontFamily from "../../constants/fonts";

interface PulseCheckHeaderProps {
  logoSource: any;
  onReturn: () => void;
  onHomePress?: () => void; // Add optional home navigation handler
  title?: string;
  subtitle?: string;
}

const { width: screenWidth } = Dimensions.get("window");

// NOTE: Styles are now generated per-theme via `createStyles(colors)` so
// this screen reads correctly in both Light and Dark mode. See
// src/theme for the semantic token definitions.
const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    header: {
      width: "100%",
      flexDirection: "column",
      alignItems: "flex-start",
      justifyContent: "flex-start",
      flexWrap: "nowrap",
      paddingHorizontal: screenWidth < 600 ? 16 : 0,
      paddingTop: screenWidth < 600 ? 16 : 16,
      paddingBottom: 8,
      borderBottomColor: colors.border.divider,
    },
    leftGroup: {
      flexDirection: "row",
      alignItems: "center",
      flex: 1,
      minWidth: 0,
      marginTop: 8,
    },
    breadcrumbContainer: {
      marginBottom: 12,
      width: "100%",
      flexDirection: "row",
      alignItems: "center",
      flexWrap: "nowrap",
      overflow: "hidden",
    },
    backButton: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: screenWidth < 600 ? 6 : 8,
      paddingHorizontal: 0,
      marginRight: 12,
    },
    backIcon: {
      fontSize: screenWidth < 600 ? 18 : 20,
      marginRight: 8,
      color: colors.brand.primary,
    },
    backButtonText: {
      fontFamily: FontFamily.regular,
      fontSize: screenWidth < 600 ? 14 : 16,
      color: colors.brand.primary,
      fontWeight: '500',
    },
    logo: {
      width: screenWidth < 600 ? 50 : 80,
      height: screenWidth < 600 ? 30 : 40,
      marginRight: 12,
    },
    textGroup: {
      flexShrink: 1,
    },
    title: {
      fontFamily: FontFamily.bold,
      fontWeight: '700',
      fontStyle: 'normal',
      fontSize: screenWidth < 600 ? 20 : 28,
      lineHeight: screenWidth < 600 ? 26 : 40,
      letterSpacing: 0,
      color: colors.text.primary,
    },
    subtitle: {
      fontFamily: FontFamily.regular,
      fontWeight: '400',
      fontStyle: 'normal',
      fontSize: screenWidth < 600 ? 12 : 16,
      lineHeight: screenWidth < 600 ? 16 : 22,
      letterSpacing: 0,
      color: colors.text.primary,
      marginTop: 2,
    },
    button: {
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: colors.brand.primary,
      paddingHorizontal: screenWidth < 600 ? 12 : 16,
      paddingVertical: screenWidth < 600 ? 8 : 10,
      borderRadius: 6,
      flexShrink: 0,
    },
    buttonIcon: {
      color: colors.text.inverse,
      fontSize: screenWidth < 600 ? 14 : 16,
      marginRight: 4,
    },
    buttonText: {
      color: colors.text.inverse,
      fontSize: screenWidth < 600 ? 12 : 14,
      fontWeight: "500",
    },
    instructionBanner: {
      backgroundColor: colors.statusBackground.info,
      textAlign: "left",
      padding: screenWidth < 600 ? 8 : 12,
      borderRadius: 6,
      // row layout: icon + text
      flexDirection: 'row',
      alignItems: 'center',
      gap: screenWidth < 600 ? 6 : 8,
      width: "100%",
      maxWidth: "100%",
      alignSelf: "center",
      height: screenWidth < 600 ? 48 : 56,
      justifyContent: "flex-start",
      paddingVertical: screenWidth < 600 ? 8 : 12,
      paddingHorizontal: screenWidth < 600 ? 12 : 16,
      marginTop: 8,
      marginBottom: 16,
    },
    infoIconCircle: {
      width: screenWidth < 600 ? 20 : 20,
      height: screenWidth < 600 ? 20 : 20,
      borderRadius: screenWidth < 600 ? 12 : 14,
      // Outline circle (no fill) using design token for border color
      backgroundColor: 'transparent',
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: screenWidth < 600 ? 8 : 12,
      borderWidth: 2,
      borderColor: colors.status.info,
    },
    infoIconText: {
      color: colors.status.info,
      fontWeight: '700',
      fontSize: screenWidth < 600 ? 12 : 14,
    },
    instructionText: {
      // Typography: Body / Small
      fontFamily: FontFamily.regular,
      fontWeight: '400',
      fontStyle: 'normal',
      fontSize: screenWidth < 600 ? 11 : 14,
      lineHeight: screenWidth < 600 ? 14 : 20,
      letterSpacing: 0,
      color: colors.text.primary,
      flex: 1,
    },
  });

const PulseCheckHeader: React.FC<PulseCheckHeaderProps> = ({
  logoSource,
  onReturn,
  onHomePress,
  title = "Pulse Check",
  subtitle: propSubtitle,
}) => {
  const dispatch = useDispatch();
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [navigationTarget, setNavigationTarget] = useState<
    'home' | 'observation'
  >('observation');

  // Get selected zone from Redux store
  const { selectedZone } = useSelector(
    (state: RootState) => state.leadershipWalk
  );

  // Get the pulseState from Redux store
  const status = useSelector((state: RootState) => state.pulseCheck.pulseState);

  // Get the selected filters
  const selectedFilters = useSelector(
    (state: RootState) => state.observation.selectedFilters
  );

  // Create subtitle from zone data if not provided as prop
  const subtitle =
    propSubtitle ||
    (selectedZone
      ? `Zones: ${selectedZone.map((zone) => zone.ZONE).join(", ")}`
      : "");

  // Handle Back click to Observation screen
  const handleReturnClick = () => {
    if (status === "completed") {
      dispatch(resetPulseCheck());
      dispatch(resetObservation());
      if (onReturn) {
        onReturn();
      }
      return;
    }
    if (status === "in-progress" || status === "new") {
      setNavigationTarget('observation');
      setShowConfirmDialog(true);
    } else {
      dispatch(resetPulseCheck());
      dispatch(resetObservation());
      if (onReturn) {
        onReturn();
      }
    }
  };

  // Handle Home navigation
  const handleHomeClick = () => {
    if (status === "completed") {
      dispatch(resetPulseCheck());
      dispatch(resetObservation());
      if (onHomePress) {
        onHomePress();
      }
      return;
    }
    if (status === "in-progress" || status === "new") {
      setNavigationTarget('home');
      setShowConfirmDialog(true);
    } else {
      dispatch(resetPulseCheck());
      dispatch(resetObservation());
      if (onHomePress) {
        onHomePress();
      }
    }
  };

  const handleConfirm = () => {
    dispatch(resetPulseCheck());
    dispatch(resetObservation());
    setShowConfirmDialog(false);

    if (navigationTarget === 'home' && onHomePress) {
      onHomePress();
    } else if (navigationTarget === 'observation' && onReturn) {
      onReturn();
    }
  };

  const handleCancel = () => {
    setShowConfirmDialog(false);
  };

  // Create breadcrumb items
  const breadcrumbItems: DLOBreadcrumbItem[] = [
    {
      id: 'observations',
      label: 'Observation',
      onPress: handleReturnClick,
    },
    {
      id: 'employee',
      label: `${selectedFilters.employee}`,
      isCurrentPage: true,
    },
  ];

  return (
    <>
      <View style={styles.header}>
        {/* Breadcrumb Navigation */}
        <View style={styles.breadcrumbContainer}>
          {screenWidth < 600 ? (
            <TouchableOpacity
              onPress={handleReturnClick}
              style={styles.backButton}
              accessibilityRole="button"
              accessibilityLabel="Back to Observation"
            >
              <Text style={styles.backIcon}>←</Text>
              <Text style={styles.backButtonText}>Back to Observation</Text>
            </TouchableOpacity>
          ) : (
            <DLOBreadcrumb
              items={breadcrumbItems}
              showHomeIcon={true}
              onHomePress={() => handleHomeClick()}
              style={{ flexDirection: "row", alignItems: "center" }}
            />
          )}
        </View>

        {/* Title and subtitle */}
        <View style={styles.leftGroup}>
          <View style={styles.textGroup}>
            <Text
              style={styles.title}
            >{`${selectedFilters.observationType}: ${selectedFilters.employee}`}</Text>
            <Text style={styles.subtitle}>{subtitle}</Text>
          </View>
        </View>
      </View>
      {/* Instruction Banner - Only show for non-completed pulse checks */}
      {status !== "completed" && (
        <View style={styles.instructionBanner}>
          <View style={styles.infoIconCircle}>
            <Text style={styles.infoIconText}>i</Text>
          </View>
          <Text style={styles.instructionText}>
            Please select any behaviors or observations you see below
          </Text>
        </View>
      )}
      <LHConfirmationDialog
        visible={showConfirmDialog}
        message="You have unsaved data, are you sure you want to go back?"
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
};

export default PulseCheckHeader;
